/**
 * Module-scoped, ref-counted body scroll lock. Multiple widget instances
 * can call lockBodyScroll() concurrently — only the first lock saves the
 * page's prior style; only the last unlock restores it. Fixes the
 * per-instance leak where two widgets opening on mobile would corrupt
 * document.body.style.overflow.
 *
 * On iOS Safari, `overflow: hidden` alone does not prevent rubber-band
 * scrolling of the body behind a fixed-position overlay. There we
 * additionally set `position: fixed; top: -${scrollY}px; width: 100%`
 * and restore the scroll position on unlock.
 *
 * Crucially, the position-fixed pin runs ONLY on iOS — applying it on
 * Android / desktop / other-mobile would reparent host-page descendants
 * positioned `absolute` against `body`, causing visible layout shifts
 * (e.g. sticky headers, cookie banners jump) for the duration of the
 * lock. On non-iOS platforms `overflow: hidden` is sufficient.
 */
let lockCount = 0;
let savedOverflow: string | null = null;
let savedPosition: string | null = null;
let savedTop: string | null = null;
let savedWidth: string | null = null;
let savedScrollX = 0;
let savedScrollY = 0;
let lockedAsIOS = false;

/** UA-detect iOS (iPhone, iPod, iPad — including iPadOS posing as Mac). */
export function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iP(ad|hone|od)/.test(ua)) return true;
  // iPadOS 13+ reports as Macintosh; touch points distinguish it from desktop.
  return /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
}

export function lockBodyScroll(): void {
  if (lockCount === 0) {
    savedOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    lockedAsIOS = isIOS();
    if (lockedAsIOS) {
      savedScrollX = window.scrollX;
      savedScrollY = window.scrollY;
      savedPosition = document.body.style.position;
      savedTop = document.body.style.top;
      savedWidth = document.body.style.width;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${savedScrollY}px`;
      document.body.style.width = '100%';
    }
  }
  lockCount += 1;
}

export function unlockBodyScroll(): void {
  if (lockCount === 0) return; // defensive — extra unlock is a no-op
  lockCount -= 1;
  if (lockCount === 0) {
    if (savedOverflow !== null) document.body.style.overflow = savedOverflow;
    if (lockedAsIOS) {
      if (savedPosition !== null) document.body.style.position = savedPosition;
      if (savedTop !== null) document.body.style.top = savedTop;
      if (savedWidth !== null) document.body.style.width = savedWidth;
      window.scrollTo(savedScrollX, savedScrollY);
    }
    savedOverflow = null;
    savedPosition = null;
    savedTop = null;
    savedWidth = null;
    savedScrollX = 0;
    savedScrollY = 0;
    lockedAsIOS = false;
  }
}

/** Test-only: reset module state. */
export function _resetForTest(): void {
  lockCount = 0;
  savedOverflow = null;
  savedPosition = null;
  savedTop = null;
  savedWidth = null;
  savedScrollX = 0;
  savedScrollY = 0;
  lockedAsIOS = false;
}
